/**
 * AUNLABEL JAIPUR - Production Interactive Storefront Engine
 * Handles AJAX Cart, Dynamic Variants, Persistent Wishlist, Drawer Sync & Quantity Controls
 */

document.addEventListener('DOMContentLoaded', () => {
  initCartDrawer();
  initWishlist();
  initSizeSelectors();
  initQuickAdd();
  initPDP();
  initQuantityControls();
  initMobileMenu();
  initMegaMenu();
  initNewArrivalsFilters();
  initScrollReveal();
});

// Format Indian Rupee currency (e.g. 279900 -> ₹2,799)
function formatMoney(cents) {
  if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
  const inr = Math.round(cents / 100);
  return '₹' + inr.toLocaleString('en-IN');
}

// -------------------------------------------------------------
// 1. CART SLIDEOUT DRAWER & AJAX ENGINE
// -------------------------------------------------------------
function openCart() {
  const cartDrawer = document.getElementById('slideover-cart');
  const cartBackdrop = document.getElementById('cart-backdrop');
  if (cartDrawer) {
    cartDrawer.classList.remove('translate-x-full');
    cartDrawer.classList.add('open');
    document.body.classList.add('cart-open');
    if (cartBackdrop) cartBackdrop.classList.add('active');
  }
}

function closeCart() {
  const cartDrawer = document.getElementById('slideover-cart');
  const cartBackdrop = document.getElementById('cart-backdrop');
  if (cartDrawer) {
    cartDrawer.classList.add('translate-x-full');
    cartDrawer.classList.remove('open');
    document.body.classList.remove('cart-open');
    if (cartBackdrop) cartBackdrop.classList.remove('active');
  }
}

function initCartDrawer() {
  const cartBackdrop = document.getElementById('cart-backdrop');
  const openButtons = document.querySelectorAll('[data-action="open-cart"]');
  const closeButtons = document.querySelectorAll('[data-action="close-cart"]');

  openButtons.forEach(btn => btn.addEventListener('click', (e) => {
    e.preventDefault();
    openCart();
  }));

  closeButtons.forEach(btn => btn.addEventListener('click', (e) => {
    e.preventDefault();
    closeCart();
  }));

  if (cartBackdrop) {
    cartBackdrop.addEventListener('click', closeCart);
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeCart();
  });

  // Delegate quantity changes and line item deletions inside cart drawer
  const cartDrawer = document.getElementById('slideover-cart');
  if (cartDrawer) {
    cartDrawer.addEventListener('click', async (e) => {
      // Quantity plus / minus
      const qtyBtn = e.target.closest('[data-cart-qty-change]');
      if (qtyBtn) {
        e.preventDefault();
        const delta = parseInt(qtyBtn.getAttribute('data-cart-qty-change'), 10);
        const line = parseInt(qtyBtn.getAttribute('data-line'), 10);
        const currentQty = parseInt(qtyBtn.getAttribute('data-qty'), 10) || 1;
        const newQty = Math.max(0, currentQty + delta);
        await updateCartLine(line, newQty);
        return;
      }

      // Remove item
      const removeBtn = e.target.closest('[data-cart-remove-line]');
      if (removeBtn) {
        e.preventDefault();
        const line = parseInt(removeBtn.getAttribute('data-cart-remove-line'), 10);
        await updateCartLine(line, 0);
      }
    });
  }
}

// Update line item in Shopify Cart API
async function updateCartLine(line, quantity) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    });
    if (!res.ok) throw new Error('Cart update failed');
    const updatedCart = await res.json();
    renderCartState(updatedCart);
  } catch (err) {
    console.error('Error updating cart line:', err);
    window.location.reload();
  }
}

// Add item to Shopify Cart API
async function addToCart(variantId, quantity = 1, triggerBtn = null) {
  if (!variantId) {
    alert('Please select a size first.');
    return;
  }

  let originalText = '';
  if (triggerBtn) {
    originalText = triggerBtn.innerHTML;
    triggerBtn.disabled = true;
    triggerBtn.innerHTML = `<span class="material-symbols-outlined text-sm animate-spin">progress_activity</span> Adding...`;
  }

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    });

    if (!res.ok) {
      const errData = await res.json();
      throw new Error(errData.description || 'Could not add to cart');
    }

    // Refresh cart data
    const cartRes = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    });
    const cartData = await cartRes.json();
    renderCartState(cartData);

    if (triggerBtn) {
      triggerBtn.innerHTML = `<span class="material-symbols-outlined text-sm">check</span> Added to Bag`;
      setTimeout(() => {
        triggerBtn.innerHTML = originalText;
        triggerBtn.disabled = false;
      }, 2000);
    }
  } catch (err) {
    alert(err.message || 'Error adding item to bag.');
    if (triggerBtn) {
      triggerBtn.innerHTML = originalText;
      triggerBtn.disabled = false;
    }
  }
}

// Re-render UI elements based on live Shopify Cart object
function renderCartState(cart) {
  // Update header and drawer count badges
  document.querySelectorAll('[data-cart-count]').forEach(el => {
    el.textContent = cart.item_count;
  });
  document.querySelectorAll('[data-cart-drawer-count]').forEach(el => {
    el.textContent = `(${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'})`;
  });

  // Update Subtotals & Checkout Price
  const subtotalFormatted = formatMoney(cart.total_price);
  const subtotalEl = document.getElementById('cart-drawer-subtotal');
  if (subtotalEl) subtotalEl.textContent = subtotalFormatted;
  const checkoutPriceEl = document.getElementById('cart-drawer-checkout-price');
  if (checkoutPriceEl) checkoutPriceEl.textContent = subtotalFormatted;

  // Free shipping progress bar
  const threshold = 99900; // ₹999 in paise
  const diff = threshold - cart.total_price;
  const progressBar = document.getElementById('shipping-progress-bar');
  const diffText = document.getElementById('shipping-diff-text');

  if (progressBar) {
    const pct = diff <= 0 ? 100 : Math.min(100, Math.round((cart.total_price / threshold) * 100));
    progressBar.style.width = pct + '%';
  }
  if (diffText) {
    if (diff <= 0) {
      diffText.innerHTML = '<span class="text-tertiary">Unlocked!</span>';
    } else {
      diffText.textContent = `${formatMoney(diff)} Away`;
    }
  }

  // Update Drawer Items Container
  const itemsContainer = document.getElementById('cart-drawer-items');
  if (itemsContainer) {
    if (cart.items.length === 0) {
      itemsContainer.innerHTML = `
        <div class="text-center py-10 space-y-3" id="cart-drawer-empty">
          <span class="material-symbols-outlined text-4xl text-outline-variant">shopping_bag</span>
          <p class="font-headline-sm text-base text-on-surface">Your Bag is Empty</p>
          <p class="font-body-sm text-on-surface-variant max-w-xs mx-auto">Explore our curated Jaipur slow-craft editions to begin your collection.</p>
          <a href="/collections/all" class="inline-block mt-2 px-space-md py-2 bg-primary text-on-primary font-label-uppercase text-xs tracking-wider uppercase hover:bg-primary-container transition-colors" data-action="close-cart">
            Discover Silhouettes
          </a>
        </div>
      `;
    } else {
      let html = '';
      cart.items.forEach((item, index) => {
        const line = index + 1;
        const imgTag = item.image 
          ? `<img src="${item.image}" alt="${item.title}" class="w-full h-full object-cover">`
          : `<span class="material-symbols-outlined text-outline text-3xl">styler</span>`;
        const variantTitle = item.variant_title ? `<p class="font-label-uppercase text-label-uppercase text-secondary tracking-widest mt-0.5">${item.variant_title}</p>` : '';

        html += `
          <div class="flex gap-space-md cart-drawer-item" data-line-index="${line}">
            <div class="w-20 h-24 bg-surface-container flex-shrink-0 flex items-center justify-center overflow-hidden">
              ${imgTag}
            </div>
            <div class="flex-1 flex flex-col justify-between">
              <div>
                <div class="flex justify-between items-start">
                  <h3 class="font-headline-sm text-base text-on-surface leading-snug">
                    <a href="${item.url}">${item.product_title}</a>
                  </h3>
                  <button type="button" class="text-outline hover:text-error transition-colors p-1" data-cart-remove-line="${line}" aria-label="Remove item">
                    <span class="material-symbols-outlined text-lg">delete</span>
                  </button>
                </div>
                ${variantTitle}
              </div>
              <div class="flex justify-between items-center mt-space-sm">
                <div class="inline-flex items-center border border-outline-variant bg-surface-bright">
                  <button type="button" class="w-7 h-7 flex items-center justify-center text-on-surface-variant hover:text-on-surface" data-cart-qty-change="-1" data-line="${line}" data-qty="${item.quantity}">-</button>
                  <span class="w-8 text-center font-label-interactive text-label-interactive text-on-surface">${item.quantity}</span>
                  <button type="button" class="w-7 h-7 flex items-center justify-center text-on-surface-variant hover:text-on-surface" data-cart-qty-change="1" data-line="${line}" data-qty="${item.quantity}">+</button>
                </div>
                <span class="font-price-display text-price-display text-on-surface">${formatMoney(item.final_line_price)}</span>
              </div>
            </div>
          </div>
        `;
      });
      itemsContainer.innerHTML = html;
    }
  }
}

// -------------------------------------------------------------
// 2. PRODUCT CARDS & QUICK ADD
// -------------------------------------------------------------
function initSizeSelectors() {
  const sizeContainers = document.querySelectorAll('[data-size-selector]');
  sizeContainers.forEach(container => {
    const buttons = container.querySelectorAll('button[data-size]');
    buttons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        buttons.forEach(b => {
          b.classList.remove('bg-primary', 'text-on-primary', 'font-medium');
          b.classList.add('bg-surface', 'text-on-surface');
        });
        btn.classList.remove('bg-surface', 'text-on-surface');
        btn.classList.add('bg-primary', 'text-on-primary', 'font-medium');

        // If inside a product card with quick-add button, sync variant id
        const card = btn.closest('[data-product-card]');
        if (card) {
          const quickAddBtn = card.querySelector('[data-action="quick-add"]');
          const variantId = btn.getAttribute('data-variant-id');
          if (quickAddBtn && variantId) {
            quickAddBtn.setAttribute('data-variant-id', variantId);
          }
        }
      });
    });
  });
}

function initQuickAdd() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-action="quick-add"]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    let variantId = btn.getAttribute('data-variant-id');
    if (!variantId) {
      // Find selected size button in card
      const card = btn.closest('[data-product-card]');
      if (card) {
        const activeSize = card.querySelector('[data-size-selector] button.bg-primary');
        if (activeSize) variantId = activeSize.getAttribute('data-variant-id');
      }
    }

    if (!variantId) {
      // Open PDP if mock or no variant selected
      const link = btn.closest('[data-product-card]')?.querySelector('a[href]');
      if (link && link.getAttribute('href') && link.getAttribute('href') !== '#') {
        window.location.href = link.href;
      }
      return;
    }

    await addToCart(variantId, 1, btn);
  });
}

// -------------------------------------------------------------
// 3. PRODUCT DETAIL PAGE (PDP) LOGIC
// -------------------------------------------------------------
function initPDP() {
  const form = document.getElementById('main-product-form');
  const variantInput = document.getElementById('selected-variant-id');
  const priceDisplay = document.getElementById('product-price');
  const comparePriceDisplay = document.getElementById('product-compare-price');
  const mainAddBtn = document.getElementById('main-add-button');
  const buyNowBtn = document.querySelector('[data-action="buy-now"]');
  const stickyBtnPrice = document.getElementById('sticky-btn-price');
  const stickyPriceDisplay = document.getElementById('sticky-price-display');

  // Multi-Option Dynamic Variant Switcher (e.g. Piece/Style + Size)
  const variantsJsonEl = document.getElementById('product-variants-json');
  let productVariants = [];
  if (variantsJsonEl) {
    try { productVariants = JSON.parse(variantsJsonEl.textContent); } catch(e){}
  }

  const optionGroups = document.querySelectorAll('#main-product-form [data-option-index]');
  if (optionGroups.length > 0 && productVariants.length > 0) {
    optionGroups.forEach(group => {
      const btns = group.querySelectorAll('button[data-option-value]');
      const label = group.querySelector('[data-option-selected-label]');
      btns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          btns.forEach(b => {
            b.classList.remove('bg-primary', 'text-on-primary', 'font-medium', 'border-primary');
            b.classList.add('bg-surface', 'text-on-surface');
          });
          btn.classList.remove('bg-surface', 'text-on-surface');
          btn.classList.add('bg-primary', 'text-on-primary', 'font-medium', 'border-primary');
          if (label) label.textContent = btn.getAttribute('data-option-value');

          const selectedOpts = [];
          document.querySelectorAll('#main-product-form [data-option-index]').forEach(g => {
            const activeBtn = g.querySelector('button.bg-primary');
            if (activeBtn) selectedOpts.push(activeBtn.getAttribute('data-option-value'));
          });

          const matchedVariant = productVariants.find(v => {
            return selectedOpts.every((optVal, idx) => v.options[idx] === optVal);
          });

          if (matchedVariant) {
            if (variantInput) variantInput.value = matchedVariant.id;
            const formattedPrice = formatMoney(matchedVariant.price);
            if (priceDisplay) priceDisplay.textContent = formattedPrice;
            if (buyNowPrice) buyNowPrice.textContent = formattedPrice;
            if (stickyBtnPrice) stickyBtnPrice.textContent = formattedPrice;
            if (stickyPriceDisplay) stickyPriceDisplay.textContent = formattedPrice;

            if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price) {
              if (comparePriceDisplay) {
                comparePriceDisplay.textContent = formatMoney(matchedVariant.compare_at_price);
                comparePriceDisplay.style.display = '';
              }
              if (discountBadge) {
                const pct = Math.round(((matchedVariant.compare_at_price - matchedVariant.price) / matchedVariant.compare_at_price) * 100);
                discountBadge.textContent = pct + '% off';
                discountBadge.style.display = '';
              }
            } else {
              if (comparePriceDisplay) comparePriceDisplay.style.display = 'none';
              if (discountBadge) discountBadge.style.display = 'none';
            }

            const addBtnText = document.getElementById('main-add-button-text');
            if (!matchedVariant.available) {
              if (mainAddBtn) {
                mainAddBtn.disabled = true;
                mainAddBtn.classList.add('opacity-50', 'cursor-not-allowed');
              }
              if (addBtnText) addBtnText.textContent = 'Sold Out';
            } else {
              if (mainAddBtn) {
                mainAddBtn.disabled = false;
                mainAddBtn.classList.remove('opacity-50', 'cursor-not-allowed');
              }
              if (addBtnText) addBtnText.textContent = 'Add to Bag';
            }
          }
        });
      });
    });
  }

  // PDP Size buttons click (Single option fallback)
  const pdpSizeBtns = document.querySelectorAll('#main-product-form [data-size-selector] button[data-size]');
  const discountBadge = document.getElementById('product-discount-badge');
  const buyNowPrice = document.getElementById('buy-now-price');

  pdpSizeBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const variantId = btn.getAttribute('data-variant-id');
      const price = btn.getAttribute('data-variant-price');
      const compare = btn.getAttribute('data-variant-compare');
      const priceRaw = parseInt(btn.getAttribute('data-variant-price-raw'), 10) || 0;
      const compareRaw = parseInt(btn.getAttribute('data-variant-compare-raw'), 10) || 0;
      const isAvailable = btn.getAttribute('data-available') !== 'false';

      if (variantInput && variantId) {
        variantInput.value = variantId;
      }
      if (priceDisplay && price) {
        priceDisplay.textContent = price;
      }
      if (buyNowPrice && price) {
        buyNowPrice.textContent = price;
      }
      if (stickyBtnPrice && price) {
        stickyBtnPrice.textContent = price;
      }
      if (stickyPriceDisplay && price) {
        stickyPriceDisplay.textContent = price;
      }

      // Update compare price & discount badge
      if (compareRaw > 0 && compareRaw > priceRaw) {
        if (comparePriceDisplay) {
          comparePriceDisplay.textContent = compare;
          comparePriceDisplay.style.display = '';
        }
        if (discountBadge) {
          const pct = Math.round(((compareRaw - priceRaw) / compareRaw) * 100);
          discountBadge.textContent = pct + '% off';
          discountBadge.style.display = '';
        }
      } else {
        // No discount for this variant
        if (comparePriceDisplay) comparePriceDisplay.style.display = 'none';
        if (discountBadge) discountBadge.style.display = 'none';
      }

      if (mainAddBtn) {
        const addBtnText = document.getElementById('main-add-button-text');
        if (!isAvailable) {
          mainAddBtn.disabled = true;
          mainAddBtn.classList.add('opacity-50', 'cursor-not-allowed');
          if (addBtnText) addBtnText.textContent = 'Sold Out';
        } else {
          mainAddBtn.disabled = false;
          mainAddBtn.classList.remove('opacity-50', 'cursor-not-allowed');
          if (addBtnText) addBtnText.textContent = 'Add to Bag';
        }
      }
    });
  });

  // Form submit -> AJAX Add to Cart
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const variantId = variantInput ? variantInput.value : form.querySelector('input[name="id"]')?.value;
      const qtyInput = document.getElementById('product-quantity-input');
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      await addToCart(variantId, qty, mainAddBtn);
    });
  }

  // Buy Now direct checkout
  if (buyNowBtn && form) {
    buyNowBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      const variantId = variantInput ? variantInput.value : form.querySelector('input[name="id"]')?.value;
      const qtyInput = document.getElementById('product-quantity-input');
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      if (!variantId) {
        alert('Please select a size.');
        return;
      }

      buyNowBtn.disabled = true;
      buyNowBtn.innerHTML = `<span class="material-symbols-outlined text-sm animate-spin">progress_activity</span> Processing...`;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: qty })
        });
        if (!res.ok) throw new Error('Checkout initialization failed');
        window.location.href = '/checkout';
      } catch (err) {
        alert(err.message || 'Error proceeding to checkout.');
        buyNowBtn.disabled = false;
        buyNowBtn.innerHTML = `Buy It Now • ${priceDisplay ? priceDisplay.textContent : ''}`;
      }
    });
  }
}

// -------------------------------------------------------------
// 4. GLOBAL QUANTITY CONTROLS
// -------------------------------------------------------------
function initQuantityControls() {
  document.addEventListener('click', (e) => {
    const minusBtn = e.target.closest('[data-quantity-minus]');
    if (minusBtn) {
      e.preventDefault();
      const container = minusBtn.closest('div');
      const input = container?.querySelector('input[type="number"]');
      if (input) {
        const current = parseInt(input.value, 10) || 1;
        input.value = Math.max(1, current - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const plusBtn = e.target.closest('[data-quantity-plus]');
    if (plusBtn) {
      e.preventDefault();
      const container = plusBtn.closest('div');
      const input = container?.querySelector('input[type="number"]');
      if (input) {
        const current = parseInt(input.value, 10) || 1;
        input.value = current + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });
}

// -------------------------------------------------------------
// 5. PERSISTENT WISHLIST & SLIDEOUT DRAWER ENGINE
// -------------------------------------------------------------
const WISHLIST_ITEMS_KEY = 'aunlabel_patron_wishlist_v3';

function getWishlistItems() {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_ITEMS_KEY)) || [];
  } catch (e) {
    return [];
  }
}

function saveWishlistItems(items) {
  try {
    localStorage.setItem(WISHLIST_ITEMS_KEY, JSON.stringify(items));
  } catch (e) {}
}

function updateWishlistCounters(count) {
  if (typeof count === 'undefined') {
    count = getWishlistItems().length;
  }
  document.querySelectorAll('[data-wishlist-count]').forEach(el => {
    el.textContent = count;
  });
  document.querySelectorAll('[data-wishlist-drawer-count]').forEach(el => {
    el.textContent = `(${count})`;
  });
}

function openWishlist() {
  const drawer = document.getElementById('slideover-wishlist');
  const backdrop = document.getElementById('wishlist-backdrop');
  const mobileMenu = document.getElementById('mobile-nav-drawer');
  if (mobileMenu) {
    mobileMenu.classList.add('hidden');
    mobileMenu.style.display = 'none';
  }

  renderWishlistState();
  if (drawer) {
    drawer.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    if (backdrop) {
      backdrop.classList.remove('hidden');
    }
    requestAnimationFrame(() => {
      drawer.classList.remove('translate-x-full');
      drawer.classList.add('open');
      document.body.classList.add('wishlist-open');
      if (backdrop) {
        backdrop.classList.remove('opacity-0', 'pointer-events-none');
        backdrop.classList.add('opacity-100');
      }
    });
  }
}

function closeWishlist() {
  const drawer = document.getElementById('slideover-wishlist');
  const backdrop = document.getElementById('wishlist-backdrop');
  if (drawer) {
    drawer.classList.remove('open');
    drawer.classList.add('translate-x-full');
    document.body.classList.remove('wishlist-open');
    document.body.style.overflow = '';
    if (backdrop) {
      backdrop.classList.remove('opacity-100');
      backdrop.classList.add('opacity-0', 'pointer-events-none');
    }
    setTimeout(() => {
      if (!drawer.classList.contains('open')) {
        drawer.classList.add('hidden');
        if (backdrop) backdrop.classList.add('hidden');
      }
    }, 320);
  }
}

function renderWishlistState() {
  const items = getWishlistItems();
  updateWishlistCounters(items.length);

  const container = document.getElementById('wishlist-drawer-items');
  if (!container) return;

  if (items.length === 0) {
    container.innerHTML = `
      <div class="text-center py-12 sm:py-16 space-y-3 px-4" id="wishlist-drawer-empty">
        <span class="material-symbols-outlined text-4xl sm:text-5xl text-outline-variant">favorite_border</span>
        <h3 class="font-headline-sm text-lg sm:text-xl text-on-surface">Your Wishlist is Empty</h3>
        <p class="font-body-sm text-xs text-on-surface-variant max-w-xs mx-auto leading-relaxed">
          Tap the heart icon on any silhouette in our catalog to curate your private atelier wishlist.
        </p>
        <a href="/collections/all" class="inline-block mt-3 px-5 py-2.5 bg-primary text-on-primary font-label-uppercase text-xs tracking-wider uppercase hover:bg-primary-container transition-colors shadow-sm" data-action="close-wishlist">
          Discover Silhouettes
        </a>
      </div>
    `;
    return;
  }

  let html = '<div class="space-y-2.5 sm:space-y-4">';
  items.forEach(item => {
    const imgHtml = item.image 
      ? `<img src="${item.image}" alt="${item.title}" class="w-full h-full object-cover">`
      : `<div class="w-full h-full bg-surface-container flex items-center justify-center"><span class="material-symbols-outlined text-outline">styler</span></div>`;

    html += `
      <div class="flex gap-2.5 sm:gap-4 p-2.5 sm:p-3 bg-surface-container-low border border-surface-container-highest transition-all relative group rounded-sm" data-wishlist-item="${item.handle}">
        <div class="w-20 h-24 sm:w-24 sm:h-28 flex-shrink-0 overflow-hidden bg-surface-container rounded-sm">
          <a href="${item.url || '/collections/all'}">${imgHtml}</a>
        </div>
        <div class="flex-1 flex flex-col justify-between min-w-0">
          <div>
            <div class="flex items-start justify-between gap-1.5">
              <h4 class="font-headline-sm text-xs sm:text-sm text-on-surface leading-snug line-clamp-2 hover:text-primary transition-colors">
                <a href="${item.url || '/collections/all'}">${item.title}</a>
              </h4>
              <button type="button" class="text-outline hover:text-error transition-colors p-1 flex-shrink-0 -mr-1 -mt-1 cursor-pointer" data-wishlist-remove="${item.handle}" aria-label="Remove from wishlist">
                <span class="material-symbols-outlined text-base sm:text-lg">delete</span>
              </button>
            </div>
            <p class="font-label-uppercase text-[9px] sm:text-[10px] text-secondary tracking-widest uppercase mt-0.5">${item.tag || 'JAIPUR ATELIER'}</p>
          </div>

          <div class="flex items-center justify-between gap-2 mt-2 pt-1.5 border-t border-surface-container-highest/60 flex-wrap sm:flex-nowrap">
            <span class="font-price-display text-xs sm:text-sm text-on-surface font-semibold">${item.price || '₹8,450'}</span>
            <a href="${item.url || '/collections/all'}" class="px-2.5 sm:px-3 py-1.5 bg-inverse-surface text-inverse-on-surface font-label-uppercase text-[9px] sm:text-[10px] tracking-wider uppercase hover:bg-primary transition-colors inline-flex items-center gap-1 whitespace-nowrap rounded-xs">
              <span>View &amp; Order</span>
              <span class="material-symbols-outlined text-xs">arrow_forward</span>
            </a>
          </div>
        </div>
      </div>
    `;
  });
  html += '</div>';
  container.innerHTML = html;
}

function initWishlist() {
  const currentItems = getWishlistItems();
  const currentHandles = currentItems.map(i => i.handle);

  // Highlight already saved items on page load
  document.querySelectorAll('[data-action="wishlist"]').forEach(btn => {
    const handle = btn.getAttribute('data-product-handle');
    if (handle && currentHandles.includes(handle)) {
      const icon = btn.querySelector('.material-symbols-outlined');
      if (icon) {
        icon.classList.add('filled');
        btn.classList.add('text-primary');
      }
    }
  });

  updateWishlistCounters(currentItems.length);

  // Wishlist heart clicks
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-action="wishlist"]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const handle = btn.getAttribute('data-product-handle') || 'patron-piece';
    let items = getWishlistItems();
    const icon = btn.querySelector('.material-symbols-outlined');
    const existingIndex = items.findIndex(i => i.handle === handle);

    if (existingIndex > -1) {
      items.splice(existingIndex, 1);
      if (icon) icon.classList.remove('filled');
      btn.classList.remove('text-primary');
    } else {
      const card = btn.closest('[data-product-card]') || document;
      const title = card.querySelector('h3 a')?.textContent?.trim() || card.querySelector('h1')?.textContent?.trim() || 'Jaipur Handcrafted Silhouette';
      const price = card.querySelector('.font-price-display')?.textContent?.trim() || card.querySelector('#product-price')?.textContent?.trim() || '₹8,450';
      const image = card.querySelector('img')?.getAttribute('src') || '';
      const url = card.querySelector('a[href*="/products/"]')?.getAttribute('href') || window.location.pathname;
      const tag = card.querySelector('.font-label-uppercase')?.textContent?.trim() || 'JAIPUR ATELIER';

      items.push({ handle, title, price, image, url, tag });
      if (icon) icon.classList.add('filled');
      btn.classList.add('text-primary');
    }

    saveWishlistItems(items);
    updateWishlistCounters(items.length);
  });

  // Global Wishlist Drawer Event Delegation
  document.addEventListener('click', (e) => {
    // Remove item from drawer
    const removeBtn = e.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      e.preventDefault();
      const handle = removeBtn.getAttribute('data-wishlist-remove');
      let items = getWishlistItems().filter(i => i.handle !== handle);
      saveWishlistItems(items);
      renderWishlistState();

      // Unhighlight heart on page if card exists
      document.querySelectorAll(`[data-action="wishlist"][data-product-handle="${handle}"]`).forEach(b => {
        const icon = b.querySelector('.material-symbols-outlined');
        if (icon) icon.classList.remove('filled');
        b.classList.remove('text-primary');
      });
      return;
    }

    // Open Wishlist Trigger
    const openTrigger = e.target.closest('[data-action="wishlist-trigger"], [data-action="open-wishlist"]');
    if (openTrigger) {
      e.preventDefault();
      openWishlist();
      return;
    }

    // Close Wishlist Trigger
    const closeTrigger = e.target.closest('[data-action="close-wishlist"]');
    if (closeTrigger) {
      e.preventDefault();
      closeWishlist();
      return;
    }

    // Backdrop Click
    if (e.target && e.target.id === 'wishlist-backdrop') {
      closeWishlist();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeWishlist();
  });
}

// -------------------------------------------------------------
// 6. HOMEPAGE NEW ARRIVALS FILTER CHIPS
// -------------------------------------------------------------
function initNewArrivalsFilters() {
  const filterButtons = document.querySelectorAll('[data-category-filters] button[data-filter]');
  if (!filterButtons.length) return;

  filterButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const filter = btn.getAttribute('data-filter').toLowerCase();

      filterButtons.forEach(b => {
        b.classList.remove('bg-inverse-surface', 'text-inverse-on-surface');
        b.classList.add('bg-surface-container-high', 'text-on-surface');
      });
      btn.classList.remove('bg-surface-container-high', 'text-on-surface');
      btn.classList.add('bg-inverse-surface', 'text-inverse-on-surface');

      // Filter product cards in the section
      const section = btn.closest('section');
      if (!section) return;
      const cards = section.querySelectorAll('[data-product-card]');

      let visibleCount = 0;
      cards.forEach(card => {
        if (filter === 'all') {
          card.style.display = '';
          visibleCount++;
        } else {
          const title = (card.getAttribute('data-title') || card.textContent).toLowerCase();
          const category = (card.getAttribute('data-category') || '').toLowerCase();
          let match = false;

          if (filter === 'kurtas') {
            match = title.includes('kurta') || title.includes('kurti') || title.includes('tunic') || title.includes('top') || title.includes('shirt') || category.includes('kurta');
          } else if (filter === 'sets') {
            match = title.includes('set') || title.includes('co-ord') || category.includes('set') || category.includes('co-ord');
          } else if (filter === 'dresses') {
            match = title.includes('dress') || title.includes('maxi') || title.includes('anarkali') || category.includes('dress');
          } else if (filter === 'bottoms') {
            match = title.includes('bottom') || title.includes('palazzo') || title.includes('pant') || title.includes('trouser') || category.includes('bottom') || category.includes('palazzo');
          }

          if (match) {
            card.style.display = '';
            visibleCount++;
          } else {
            card.style.display = 'none';
          }
        }
      });

      const emptyEl = section.querySelector('[data-category-empty]');
      if (emptyEl) {
        if (visibleCount === 0) {
          emptyEl.classList.remove('hidden');
        } else {
          emptyEl.classList.add('hidden');
        }
      }
    });
  });
}

// -------------------------------------------------------------
// 7. MOBILE MENU NAVIGATION
// -------------------------------------------------------------
function initMobileMenu() {
  const menuBtn = document.querySelector('[data-action="toggle-mobile-menu"]');
  const mobileMenu = document.getElementById('mobile-nav-drawer');
  const closeMenuBtn = document.querySelector('[data-action="close-mobile-menu"]');

  const openMobileMenu = () => {
    if (!mobileMenu) return;
    mobileMenu.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
  };

  const closeMobileMenu = () => {
    if (!mobileMenu) return;
    mobileMenu.classList.add('hidden');
    document.body.style.overflow = '';
  };

  if (menuBtn && mobileMenu) {
    menuBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (mobileMenu.classList.contains('hidden')) {
        openMobileMenu();
      } else {
        closeMobileMenu();
      }
    });
  }

  if (closeMenuBtn && mobileMenu) {
    closeMenuBtn.addEventListener('click', (e) => {
      e.preventDefault();
      closeMobileMenu();
    });
  }

  // Auto-close mobile drawer when any link or action button inside is clicked
  if (mobileMenu) {
    const actionElements = mobileMenu.querySelectorAll('a, button[data-action]');
    actionElements.forEach(el => {
      el.addEventListener('click', () => {
        closeMobileMenu();
      });
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !mobileMenu.classList.contains('hidden')) {
        closeMobileMenu();
      }
    });

    // Auto-close when viewport expands to desktop
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 1024 && !mobileMenu.classList.contains('hidden')) {
        closeMobileMenu();
      }
    });
  }
}

// -------------------------------------------------------------
// 7b. DESKTOP FULL-SCREEN MEGA-MENU
// -------------------------------------------------------------
function initMegaMenu() {
  const containers = document.querySelectorAll('[data-dropdown-container="collections"], .group');
  containers.forEach(container => {
    const trigger = container.querySelector('[data-dropdown-trigger="collections"]');
    const menu = container.querySelector('.mega-menu-dropdown, #collections-mega-menu');
    if (!trigger || !menu) return;

    let timeoutId = null;

    const openMenu = () => {
      clearTimeout(timeoutId);
      menu.classList.remove('hidden');
      menu.classList.add('!block');
      trigger.setAttribute('aria-expanded', 'true');
    };

    const closeMenu = () => {
      timeoutId = setTimeout(() => {
        menu.classList.remove('!block');
        menu.classList.add('hidden');
        trigger.setAttribute('aria-expanded', 'false');
      }, 180);
    };

    // Open immediately on mouse enter
    container.addEventListener('mouseenter', openMenu);
    container.addEventListener('mouseleave', closeMenu);
    menu.addEventListener('mouseenter', openMenu);
    menu.addEventListener('mouseleave', closeMenu);

    // Also support click toggle on desktop
    trigger.addEventListener('click', (e) => {
      if (window.innerWidth >= 1024) {
        e.preventDefault();
        const isOpen = menu.classList.contains('!block') && !menu.classList.contains('hidden');
        if (isOpen) {
          clearTimeout(timeoutId);
          menu.classList.remove('!block');
          menu.classList.add('hidden');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          openMenu();
        }
      }
    });
  });

  // Close dropdown on click outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-dropdown-container="collections"]') && !e.target.closest('.mega-menu-dropdown')) {
      document.querySelectorAll('.mega-menu-dropdown, #collections-mega-menu').forEach(menu => {
        menu.classList.remove('!block');
        menu.classList.add('hidden');
      });
      document.querySelectorAll('[data-dropdown-trigger="collections"]').forEach(t => {
        t.setAttribute('aria-expanded', 'false');
      });
    }
  });

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('.mega-menu-dropdown, #collections-mega-menu').forEach(menu => {
        menu.classList.remove('!block');
        menu.classList.add('hidden');
      });
      document.querySelectorAll('[data-dropdown-trigger="collections"]').forEach(t => {
        t.setAttribute('aria-expanded', 'false');
      });
    }
  });
}

// -------------------------------------------------------------
// 8. SCROLL-BASED REVEAL ANIMATIONS ENGINE
// -------------------------------------------------------------
function initScrollReveal() {
  const revealElements = document.querySelectorAll(
    '.reveal-on-scroll, .reveal-fade, .reveal-scale, .reveal-slide-left, .reveal-slide-right, .reveal-stagger, .reveal-row-stagger, .reveal-curtain, .reveal-bloom, .reveal-filmstrip, .reveal-bento, .reveal-deep-rise, .reveal-cinematic, .reveal-parallax-primary, .reveal-parallax-accent'
  );
  if (!revealElements.length) return;

  // Fallback for environments lacking IntersectionObserver
  if (!('IntersectionObserver' in window)) {
    revealElements.forEach(el => el.classList.add('is-revealed'));
    return;
  }

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        // Synchronize paired parallax elements in the same container
        if (entry.target.parentElement) {
          entry.target.parentElement.querySelectorAll('.reveal-parallax-primary, .reveal-parallax-accent').forEach(p => {
            p.classList.add('is-revealed');
            obs.unobserve(p);
          });
        }
        obs.unobserve(entry.target);
      }
    });
  }, {
    root: null,
    rootMargin: '0px 0px -40px 0px',
    threshold: 0.08
  });

  revealElements.forEach(el => {
    const rect = el.getBoundingClientRect();
    // If element is already visible in viewport on initial load, reveal immediately
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      el.classList.add('is-revealed');
    } else {
      observer.observe(el);
    }
  });
}
